import React, { Component } from 'react';
import { Button, Select, Space, Alert, Modal, Spin } from 'antd';
import { useState, useEffect, useRef } from 'react';
import {useQuill} from 'react-quilljs';
import 'quill/dist/quill.snow.css'; // Add css for snow theme

import { ConstantsAssamese } from '../constants/constantsAssamese';
import { ConstantsBengali } from '../constants/constantsBengali';
import { ConstantsHindi } from '../constants/constantsHindi';
import { ConstantsMalayalam } from '../constants/constantsMalayalam';
import { ConstantsMarathi } from '../constants/constantsMarathi';
import { ConstantsOdia } from '../constants/constantsOdia';
import { ConstantsTamil } from '../constants/constantsTamil';
import AdUnit from './AdUnit';
import axios from "axios";

const WORD_LIMIT = 413;
const FAST_WORD_LIMIT = 613;
const SUGGESTION_LIMIT = 8;
const placeholder = 'Write or paste text...(Please limit to 413 words)';

const Constants = {
    "Assamese": ConstantsAssamese,
    "Bengali": ConstantsBengali,
    "Hindi": ConstantsHindi,
    "Malayalam": ConstantsMalayalam,
    "Marathi": ConstantsMarathi,
    "Odia": ConstantsOdia,
    "Tamil": ConstantsTamil
};

let language = "Assamese";
let dictionaries = {}
let current_constants = Constants[language];
let unique_words = '';
let timer;
let wrong_words = [];
let upload_timer;
let whole_spell_check = false;
let space_positions = [];
let suggestion = [];

export const SpellChecker =  () => {
    const theme = 'snow'; // 'bubble'
    const modules = {
        toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ],
    };

    const { quill, quillRef, Quill } = useQuill({ theme, modules, placeholder });
    const { quill: fastQuill, quillRef: fastQuillRef } = useQuill({
        theme: 'snow',
        modules: { toolbar: false },
        placeholder: 'Paste text here for speedy check (no suggestions).'
    });

    const [fastWordCount, setFastWordCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fastLoading, setFastLoading] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    // Pause spell-check while modal is open
    const spellCheckPausedRef = useRef(false);
    const fastAdjustingRef = useRef(false);
    const fastPasteInProgressRef = useRef(false);
    const fastCrossCheckedRef = useRef(false);
    const betweenBoxesAdRef = useRef(null);
    let wordCacheRef = useRef(new Map()); // stable across renders
    let ignoredWordsRef = useRef(new Set());

    const disabled_names = ["Malayalam", "Marathi", "Odia", "Tamil"];

    const dict_names = ["Assamese", "Bengali", "Hindi", "Malayalam", "Marathi", "Odia", "Tamil"];

    const countWords = (text = '') => {
        const cleaned = text.trim();
        if (!cleaned) return 0;
        return cleaned.split(/\s+/).filter(Boolean).length;
    };

    const normalizeNewlines = (text = '') => text.replace(/\r\n/g, '\n');

    const trimToWordLimit = (text = '', limit = WORD_LIMIT) => {
        if (limit <= 0) return '';
        let wordsSeen = 0;
        let output = '';
        const tokens = text.match(/(\s+|\S+)/g) || [];

        for (const token of tokens) {
            const isSpace = /^\s+$/.test(token);
            if (isSpace) {
                if (output.length) output += token;
                continue;
            }

            wordsSeen += 1;
            if (wordsSeen > limit) break;
            output += token;
        }

        return output;
    };

    const applyFastPasteLimit = (rawText = '', existingText = '') => {
        const normalized = normalizeNewlines(rawText || '');
        const existingWords = countWords((existingText || '').trim());
        const allowed = FAST_WORD_LIMIT - existingWords;
        if (allowed <= 0) return '';
        const pastedWords = countWords(normalized);
        if (pastedWords > allowed) {
            return trimToWordLimit(normalized, allowed);
        }
        return normalized;
    };

    const insertFastPlainText = (rawText = '') => {
        if (!fastQuill || !Quill) return false;
        const Delta = Quill.import('delta');
        const selection = fastQuill.getSelection(true) || {
            index: fastQuill.getLength(),
            length: 0
        };
        const currentText = fastQuill.getText();
        const textForLimit = selection.length
            ? currentText.slice(0, selection.index) + currentText.slice(selection.index + selection.length)
            : currentText;
        const textToInsert = applyFastPasteLimit(rawText, textForLimit);
        if (!textToInsert) return false;
        const delta = new Delta()
            .retain(selection.index)
            .delete(selection.length || 0)
            .insert(textToInsert);
        fastQuill.updateContents(delta, 'user');
        const maxIndex = Math.max(0, fastQuill.getLength() - 1);
        const nextIndex = Math.min(selection.index + textToInsert.length, maxIndex);
        fastQuill.setSelection(nextIndex, 0, 'silent');
        fastQuill.focus();
        return true;
    };

    const RE_HAS_LATIN   = /\p{Script=Latin}/u;
    const RE_ONLY_LATINY = /^[\p{Script=Latin}0-9'._\-\/]+$/u;
    const RE_HAS_INDIC   = /[\u0980-\u09FF]/u;  // Bengali/Assamese block

    function isEnglishLikeToken(w) {
        if (!w) return false;
        const core = w.normalize('NFC');
        return RE_HAS_LATIN.test(core) &&
                !RE_HAS_INDIC.test(core) &&
                RE_ONLY_LATINY.test(core);
    }

    const normalizeApostrophes = (text = '') => text.replace(/['\u2018]/g, '\u2019');
    const apostropheVariants = (text = '') => {
        const primary = normalizeApostrophes(text);
        const ascii = primary.replace(/\u2019/g, "'");
        return primary === ascii ? [primary] : [primary, ascii];
    };

    // ADDING 0 - Locate files to client\public\resource\LANG+Cate\

    // ADDING 1 - Add new categories, subcategories, and suffixes here. Ensure that we are adding same number of categories, subcategories, and suffixes in the respective arrays at the same time. If we add a new category, we should also add a new subcategory and suffix to maintain the same length in all three arrays though some of them are empty.
    const cate_names = [
        "Cate1NNLkhini", "Cate2NNLkhila", "Cate3NNLkhan", "Cate4NNLjopa", "Cate5NNLdora", "Cate6NNL", "Cate7NL", "Cate8NLH", "Cate9NHnr", "Cate10NonHnr", "Cate11AAANoun", "Cate12VByan", "Cate13VSwar", "Cate14VAa", "Cate15VE", "Cate16VO", "Cate17VPasani", "Cate18Extra", "Cate19Extra", "Cate20Extra", "Cate21Extra", "Cate22Extra", "Cate23Extra", "Cate24Extra", "Prefix25", "Prefix26", "Cate27Extra", "Cate28Extra", "Cate29Extra", "Cate30Extra", "Cate31Extra", "Cate32Extra", "Cate33Extra", "Cate34Extra", "Cate35Extra", "Cate36Extra", "Cate37Extra", "Cate38Extra" // Add new categories here
    ];
    const sub_cate_names = [
        "subCate1", "subCate2", "subCate3", "subCate4", "subCate5", "subCate6", "subCate7", "subCate8", "subCate9", "subCate10", "subCate11", "subCate12", "subCate13", "subCate14", "subCate15", "subCate16", "subCate17", "subCate18", "subCate19", "subCate20", "subCate21", "subCate22", "subCate23", "subCate24", "subCate25Pfx", "subCate26Pfx", "subCate27", "subCate28", "subCate29", "subCate30", "subCate31", "subCate32", "subCate33", "subCate34", "subCate35", "subCate36", "subCate37", "subCate38" // Add new subcategories here
    ];
    const suffix_names = [
        "Suffix1", "Suffix2", "Suffix3", "Suffix4", "Suffix5", "Suffix6", "Suffix7", "Suffix8", "Suffix9", "Suffix10", "Suffix11", "Suffix12", "Suffix13", "Suffix14", "Suffix15", "Suffix16", "Suffix17", "Suffix18", "Suffix19", "Suffix20", "Suffix21", "Suffix22", "Suffix23", "Suffix24", "Suffix25", "Suffix26", "Suffix27", "Suffix28", "Suffix29", "Suffix30", "Suffix31", "Suffix32", "Suffix33", "Suffix34", "Suffix35", "Suffix36", "Suffix37", "Suffix38" // Add new Suffixes here
    ];
    
    const categoriesRef = useRef(Array(cate_names.length).fill().map(() => []));
    const subCategoriesRef = useRef(Array(sub_cate_names.length).fill().map(() => []));
    const suffixesRef = useRef(Array(suffix_names.length).fill().map(() => []));

    const wCategoriesRef = useRef(Array(cate_names.length).fill().map(() => []));
    const wSubCategoriesRef = useRef(Array(sub_cate_names.length).fill().map(() => []));
    const wSuffixesRef = useRef(Array(suffix_names.length).fill().map(() => []));
    
    
    const findWordPos = (source, cateN) => {
        let indices = [];
        for (let i = 0; i < cateN.length; i++) {
            if (source.indexOf(cateN[i]) === 0 && cateN[i] !== '') {
                indices.push(i);
            }
        }
        return indices;
    };

    const checkJoinedWord = (word) => {
        // Correct when 
        // 1. word in Categories, Cate+subCate, Cate+ Suffix, Cate+subCate+Suffix
        // 2. If the word is in Prefix (25, 26), then the word is only showed correct when Prefix + SubCate + Suffix or Prefix + SubCate  or Prefix + Suffix is true.
        const categories = categoriesRef.current;
        const subCategories = subCategoriesRef.current;
        const suffixes = suffixesRef.current;
        let tmp_word = word;

        console.log("[checkJoinedWord]: ", word)

        // Iterate through all Categories, Sub Categories and Suffixes
        for (let p = 0; p < categories.length; p++) {
            const categoryList = categories[p] || [];
            const subCategoryList = subCategories[p] || [];
            const suffixList = suffixes[p] || [];
            let indices = findWordPos(word, categoryList)
            if (p==0) {
                console.log('categories[p]')
                console.log(categoryList)
            }
            for (let i = 0; i < indices.length; i++) {
                tmp_word = word
                tmp_word = tmp_word.replace(categoryList[indices[i]], '')
                if (tmp_word == '' && p != 24 && p != 25) return true
                let indices1 = findWordPos(tmp_word, subCategoryList)

                for (let j = 0; j < indices1.length; j++) {
                    let tmp_word1 = tmp_word
                    tmp_word1 = tmp_word1.replace(subCategoryList[indices1[j]], '')
                    if (tmp_word1 == '') return true
                      let indices2 = findWordPos(tmp_word1, suffixList)
                  for (let k = 0; k < indices2.length; k++) {
                        let tmp_word2 = tmp_word1.replace(suffixList[indices2[k]], '')
                        if (tmp_word2 == '') return true
                    }
                }
//Comment out this part if Prefix+Suffix should not be true
              /*  let indices2 = findWordPos(tmp_word, suffixList)
                for (let k = 0; k < indices2.length; k++) {
                    let tmp_word2 = tmp_word.replace(suffixList[indices2[k]], '')
                    if (tmp_word2 == '') return true
                }*/
            }
        }

        return false
    }
    const exceptions_in_checking = ["মায়াময়", "মায়াময়"];

    const crossCheckedRef = useRef(false);
    // 1) Define cross‐language mappings as arrays:
    const crossLangMap = {
        Assamese: [
        {
           // detectChar: 'য়',      // Bengali ‘র’
            //replaceChar: 'য়',     // Assamese ‘ৰ’
            //detectChar: 'র',      // Bengali ‘র’
            //replaceChar: 'ৰ',
            detectChar: '\u09B0',      // Bengali ‘র’
            replaceChar: '\u09F0',     // Assamese ‘ৰ’
            detectedName: 'Bengali র',
            targetName: 'Assamese ৰ'
        }
        // If we ever have more mappings, just add another object here
        ],
        Bengali: [
        {
            detectChar: '\u09F0',      // Assamese ‘ৰ’
            replaceChar: '\u09B0',     // Bengali ‘র’
            detectedName: 'Assamese য়',
            targetName: 'Bengali য়'
        }
        ]
    };

    const isWordValidBase = (w) => {
        const normalized = normalizeApostrophes(w);
        if (ignoredWordsRef.current.has(normalized)) return true;        // <-- skip spell-check if user ignored it
        // a) numeric or Assamese-numeric
        if (isNumeric(normalized)) return true;
    
        // b) direct dictionary lookup
        const tests = language === "Assamese"
        ? [` ${normalized} `, `\n${normalized} `, ` ${normalized}\n`]
        : [`${normalized}`, `\n${normalized} `, ` ${normalized}\n`];
        if (tests.some(t => unique_words.includes(t))) return true;
    
        // c) joined-word logic
        if (checkJoinedWord(normalized)) return true;
    
        return false;
    };
    

    const isWordValid = (w) => {
        const normalized = normalizeApostrophes(w);
        // 2.1) If base says OK, we’re done
        if (isWordValidBase(normalized)) return true;

        // 2.2) Try each variation **once**, but only via the base checker
        for (let key in current_constants.variations) {
        const normalizedKey = normalizeApostrophes(key);
        // make an array of replacement values
        const vals = Array.isArray(current_constants.variations[key])
            ? current_constants.variations[key]
            : [current_constants.variations[key]];
        const normalizedVals = vals.map(v => normalizeApostrophes(v));

        // a) if normalized contains the dictionary key, try swapping to each val
        if (normalized.includes(normalizedKey)) {
            for (let val of normalizedVals) {
            const candidate = normalized.replace(normalizedKey, val);
            if (isWordValidBase(candidate)) return true;
            }
        }
        // b) if normalized contains a variant val, swap back to the key
        for (let val of normalizedVals) {
            if (normalized.includes(val)) {
            const candidate = normalized.replace(val, normalizedKey);
            if (isWordValidBase(candidate)) return true;
            }
        }
        }

        // nothing matched
        return false;
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////////
    // Load Dictionaries and set unique words with Assamese as default
    let options = {
        method: 'GET',
        url: '',
    };

    dict_names.map (each => {
        if (!dictionaries[each]) {
            options.url = 'resource/' + each + '-Words.txt'
            axios.request(options).then(function (response) {
                dictionaries[each] = response.data
                if (unique_words.length == 0 && each == language) {
                    saveUniqueWords(response.data);
                }
            }).catch(function (error) {
                console.error(error);
            });
        }
    })
    // End of Load Dictionaries
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////


    useEffect(() => {
        if (quill) {
            
            const Delta = Quill.import("delta");

            quill.root.removeEventListener('contextmenu', e => e.preventDefault());
            quill.root.addEventListener('contextmenu', e => {
                e.preventDefault();
              });

            let tooltip = quill.theme.tooltip;

            // whenever anything inside the tooltip is clicked, hide the tooltip
            tooltip.root.addEventListener('click', () => { console.log('hhhhhhhhhhhhhhh')
                tooltip.hide();
                tooltip.root.style.display = 'none';
            });

            function handleClickOutside(e) {
                // if the click is anywhere but inside the tooltip, hide it
                if (tooltip.root && !tooltip.root.contains(e.target)) {
                  tooltip.hide();
                  tooltip.root.style.display = 'none';
                }
            }

            document.removeEventListener('mousedown', handleClickOutside);
            document.addEventListener('mousedown', handleClickOutside);

            const updateWordCount = () => {
                setWordCount(countWords(quill.getText()));
            };

            const showWordLimitNotice = () => {
                Modal.info({
                    title: `Only ${WORD_LIMIT} words are pasted.`,
                });
            };

            const applyPasteLimit = (rawText) => {
                const normalized = normalizeNewlines(rawText || '');
                const existingWords = countWords((quill.getText() || '').trim());
                const allowed = WORD_LIMIT - existingWords;
                if (allowed <= 0) {
                    showWordLimitNotice();
                    return '';
                }

                const pastedWords = countWords(normalized);
                if (pastedWords > allowed) {
                    showWordLimitNotice();
                    return trimToWordLimit(normalized, allowed);
                }

                return normalized;
            };

            const runRangeCheck = (cursor, lengthToCheck) => {
                setLoading(true);
                setTimeout(async () => {
                    await new Promise(r => setTimeout(r, 60));
                    recheckRange(cursor, lengthToCheck);
                    clearTimeout(upload_timer);
                    upload_timer = setTimeout(uploadWrongWords, 5000);
                    setLoading(false);
                }, 0);
            };

            const processPastedText = (rawText, cursorOverride) => {
                const textToInsert = applyPasteLimit(rawText);
                if (!textToInsert) return;

                const cursor = typeof cursorOverride === 'number'
                    ? cursorOverride
                    : (quill.getSelection(true)?.index ?? quill.getLength());

                quill.insertText(cursor, textToInsert, 'silent');
                quill.setSelection(cursor + textToInsert.length, 0);
                updateWordCount();

                const mappings = crossLangMap[language] || [];
                const offenders = mappings.filter(({ detectChar }) => textToInsert.includes(detectChar));
                const [primary] = offenders;

                if (offenders.length && !crossCheckedRef.current) {
                    // Pause spell-check; show modal instantly (no heavy work yet)
                    spellCheckPausedRef.current = true;
                    crossCheckedRef.current = true;

                    Modal.confirm({
                        title: `${primary.detectedName} detected`,
                        content: `Replace all ${primary.detectChar} before spell-checking?`,
                        okText: 'Yes',
                        cancelText: 'No',
                        onOk() {
                            // FAST replace without full re-check
                            offenders.forEach(({ detectChar, replaceChar }) => {
                                changeAll(detectChar, replaceChar, /* exactMatch */ true);
                            });
                            spellCheckPausedRef.current = false;
                            runRangeCheck(cursor, textToInsert.length);
                        },
                        onCancel() {
                            spellCheckPausedRef.current = false;
                            runRangeCheck(cursor, textToInsert.length);
                        }
                    });

                    return;
                    }

                runRangeCheck(cursor, textToInsert.length);
            };

            updateWordCount();

            // now grab the Paste button and override its click
            const pasteBtn = document.getElementById('paste-btn');
            async function handlePasteClick(e) {
                try {
                    const raw  = await navigator.clipboard.readText();
                    processPastedText(raw);
                } catch (err) {
                    console.error(err);
                }
            }
              
            pasteBtn.removeEventListener('click', handlePasteClick);
            pasteBtn.addEventListener('click', handlePasteClick);

            // now grab the ReCheck button and override its click
            const reCheckBtn = document.getElementById('recheck-btn');
            async function handleReCheckClick(e) {
                setLoading(true);
                // let spinner appear
                await new Promise(r => setTimeout(r, 100));
                try {
                    const text = quill.getText(); console.log('text=<<', text, '>>')

                    // now run a quick scan over exactly what we just inserted:
                    let offset = 0;
                    text.split(/(\s+)/).forEach(chunk => {
                    if (chunk.trim()) {
                        checkWord(chunk, offset, offset + chunk.length, ' ');
                    }
                    offset += chunk.length;
                    });

                    quill.focus();
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            }
            reCheckBtn.removeEventListener('click', handleReCheckClick);
            reCheckBtn.addEventListener('click', handleReCheckClick);

            if (suggestion.length == 0) getSuggestion()

            ////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // Load all Categories, SubCategories and Suffixes for each dictionary
            const categories = categoriesRef.current;
            const subCategories = subCategoriesRef.current;
            const suffixes = suffixesRef.current;
            const wCategories = wCategoriesRef.current;
            const wSubCategories = wSubCategoriesRef.current;
            const wSuffixes = wSuffixesRef.current;
            if (categories[0].length == 0) {
                dict_names.map(each => {
                    for(let i = 0; i < categories.length; i++) {
                        // Category
                        options.url = 'resource/' + each + 'Cate/' + cate_names[i] + '.txt';
                        axios.request(options).then(function (response) {
                            wCategories[i][each] = response.data.split(/(?:,|\s|[\r\n]+)/);
                            if (each == language) categories[i] = wCategories[i][each] || [];
                        }).catch(function (error) {
                            console.error(error);
                        });
                        // Sub Category
                        options.url = 'resource/' + each + 'Cate/' + sub_cate_names[i] + '.txt';
                        axios.request(options).then(function (response) {
                            wSubCategories[i][each] = response.data.split(/\r?\n/)
                            if (each == language) subCategories[i] = wSubCategories[i][each] || [];
                        }).catch(function (error) {
                            console.error(error);
                        });
                        // Suffix
                        options.url = 'resource/' + each + 'Cate/' + suffix_names[i] + '.txt';
                        axios.request(options).then(function (response) {
                            wSuffixes[i][each] = response.data.split(/\r?\n/)
                            if (each == language) suffixes[i] = wSuffixes[i][each] || [];
                        }).catch(function (error) {
                            console.error(error);
                        });
                    }
                })
            }
            // End of Load
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////

            const bindings={
                // custom1:{
                //     key: ' ',
                //     handler:function(range, context) {
                //         if(range.length==0)
                //         {
                //             quill.insertText(range.index, ' ');
                //             spellCheck(range.index, 'insert',' ');
                //         }
                //         else
                //         {
                //             quill.deleteText(range.index, range.length);
                //             spellCheck(range.index, 'delete','');
                //         }
                //     }
                // },
                custom2:{
                    key: 'backspace',
                    handler:function(range, context) {
                        if(range.length==0)
                        {
                            quill.deleteText(range.index-1, 1);
                            spellCheck(range.index, 'delete','');
                        }
                        else
                        {
                            quill.deleteText(range.index, range.length);
                            // spellCheck(range.index, 'delete','');
                        }
                    }
                },
                custom3: {
                    key: 'delete',
                    function(range, context) {
                        if(range.length==0)
                        {
                            quill.deleteText(range.index-1, 1);
                            // spellCheck(range.index, 'delete','',1);
                        }
                        else
                        {
                            quill.deleteText(range.index, range.length);
                            // spellCheck(range.index, 'delete','');
                        }
                    }
                },
            }
            // quill.getModule('keyboard').bindings = bindings;
            quill.keyboard.addBinding(bindings);
            // quill.getModule('history').delay = 1000;
            // quill.getModule('history').maxStack = 500;
            // quill.getModule('history').userOnly = true;

            // recheck only a slice of text [start, start+len)
            function recheckRange(start, len) {
            const block = quill.getText(start, len);
            let offset = start;
            block.split(/(\s+)/).forEach(chunk => {
                if (chunk.trim()) {
                const wStart = offset + (chunk.indexOf(chunk.trim()));
                const wEnd   = wStart + chunk.trim().length;
                checkWord(chunk.trim(), wStart, wEnd, ' ');
                }
                offset += chunk.length;
            });
            }

            const handlePaste = (event) => {
                if (!event || !event.clipboardData) return;
                if (event.target && !quill.root.contains(event.target)) return;
                event.preventDefault();

                const raw = event.clipboardData.getData('text/plain');
                processPastedText(raw);
            };              

            const handleCut = (e) => {
                if (!e || !e.clipboardData) return;
                if (e.target && !quill.root.contains(e.target)) return;
                const range = quill.getSelection();
                if (!range || range.length === 0) return;
                const text = quill.getText(range.index, range.length);
                const new_text = preActionCopy(text);
                let index = range.index;
                let length = range.length;
                let delta = new Delta()
                    .retain(index)
                    .delete(length);
                quill.updateContents(delta);
                e.clipboardData.setData('text/plain', new_text); // e.clipboardData.getData('text/plain')
                e.preventDefault()
            }

            // Replace every exact match of `oldW` with `newW` in the editor
            function escapeRegExp(str) {
                return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            }

            // Replace all (exact or fuzzy) and, when exactMatch=true, restyle every newW as correct
            function changeAll(oldW, newW, exactMatch = false) {
                const escOld = escapeRegExp(oldW);
                const boundary = '\\s\\.,!\\?:;\\|।-—৷';
                // capture prefix boundary, the word, then optional punc in group 3
                const matcher = exactMatch
                    ? new RegExp(`(^|[${boundary}])(${escOld})(?:([\\,\\|।-—৷]|!{1,2}))?(?=$|[${boundary}])`, 'g')
                    : new RegExp(escOld, 'g');

                const orig = quill.getContents();
                const output = new Delta();

                orig.ops.forEach(op => {
                    if (typeof op.insert === 'string') {
                    let str = op.insert, last = 0, m;
                    while ((m = matcher.exec(str)) !== null) {
                        const [full, pre, word, punc] = m;
                        // 1) prefix
                        if (m.index > last) {
                        output.insert(str.slice(last, m.index), op.attributes);
                        }
                        // 2) prefix boundary char (if any)
                        if (pre) {
                        output.insert(pre, op.attributes);
                        }
                        // 3) replacement + punctuation
                        const attrs = { ...(op.attributes||{}) };
                        attrs.color     = '#111';
                        attrs.underline = false;
                        output.insert(newW + (punc||''), attrs);

                        last = m.index + full.length;
                    }
                    // 4) tail
                    if (last < str.length) {
                        output.insert(str.slice(last), op.attributes);
                    }
                    } else {
                    output.insert(op.insert, op.attributes);
                    }
                });

                // 1) replace the editor contents
                quill.setContents(output, 'silent');
                // 2) grab the new plain-text and re-spellcheck it
                if (!exactMatch) {
                  const text = quill.getText();
                  let offset = 0;
                  text.split(/(\s+)/).forEach(chunk => {
                    if (chunk.trim()) {
                      const start = offset;
                      const end   = offset + chunk.length;
                      checkWord(chunk, start, end, ' ');
                    }
                    offset += chunk.length;
                  });
                }
                // 3) restore the cursor & focus if like
                const newCursor = quill.getLength();
                quill.setSelection(newCursor, 0);
                quill.focus();
            }

            // Add to in-memory ignore list & clear styling on every occurrence
            function ignoreAll(w) {
                const normalized = normalizeApostrophes((w || '').trim());
                const variants = [...new Set(apostropheVariants(normalized))];
                // 1) remember it in local ignore-list
                ignoredWordsRef.current.add(normalized)
              
                variants.forEach(term => {
                    // 2) build a whole-word regex (same boundary rules as changeAll)
                    const esc = escapeRegExp(term);
                    const boundary = "\\s\\.,!\\?:;\\|\\-\\u0964\\u0965\\u09F7\\u2014";
                    const pattern = new RegExp(
                      `(^|[${boundary}])(${esc})(?:([\\,\\|\\u0964\\u0965\\u09F7\\u2014]|!{1,2}))?(?=$|[${boundary}])`,
                      'g'
                    );
                    // 3) scan the full text, clear styling for each match
                    const full = quill.getText();
                    let m;
                    while ((m = pattern.exec(full)) !== null) {
                        const boundary = m[1];
                        const wordLen  = m[2].length;
                        const puncLen  = m[3] ? m[3].length : 0;       // now handles '!!' (2)
                        const idx      = m.index + boundary.length;
                        quill.formatText(idx, wordLen + puncLen, {
                            color: '#111',
                            underline: false
                        }, 'silent');
                    }
                });
              }
            const getMachineSuggestion = (word, required_num = SUGGESTION_LIMIT) => {
                let machine_suggestions = []
                let is_suggestion = false
                // First, check if replacement works - If replacement works, returning one suggestion rather than 4 suggestions
                for (let key in current_constants.suggestion_replacement) {
                    let tmp_word = word
                    let values = []
                    let is_contained = word.indexOf(key)
                    if (is_contained >= 0) {
                        if (current_constants.suggestion_replacement[key] instanceof Array) {
                            current_constants.suggestion_replacement[key].map(each => {
                                values.push(each)
                            })
                        } else {
                            values.push(current_constants.suggestion_replacement[key])
                        }

                        values.map(each => {
                            tmp_word = word
                            let replace_word = tmp_word.replace(key, each)
                            is_suggestion = checkMachineSuggestion(replace_word)
                            if (is_suggestion) {
                                machine_suggestions.push(replace_word)
                            }
                        })

                        if (machine_suggestions.length > required_num) return machine_suggestions
                    }
                }

                // Second, check if addition, removal or position exchange works
                let tmp_word = word.substr(0, word.length - 1)
                is_suggestion = checkMachineSuggestion(tmp_word)
                if (is_suggestion) {
                    machine_suggestions.push(tmp_word)
                    if (machine_suggestions.length >= required_num) return machine_suggestions
                }

                is_suggestion = checkMachineSuggestion(word + " ো")
                if (is_suggestion) {
                    machine_suggestions.push(tmp_word)
                    if (machine_suggestions.length >= required_num) return machine_suggestions
                }

                if (word.length >= 2) {
                    tmp_word = word[1] + word[0] + word.substr(2, word.length - 2)
                    is_suggestion = checkMachineSuggestion(tmp_word)
                    if (is_suggestion) {
                        machine_suggestions.push(tmp_word)
                        if (machine_suggestions.length >= required_num) return machine_suggestions
                    }
                }

                // Third, fetch words by the initial character match
                tmp_word = word
                while (machine_suggestions.length < required_num && tmp_word.length > 1) {
                    tmp_word = tmp_word.substr(0, tmp_word.length - 1)
                    const char_matched = suggestion.filter(each => each.word.includes(tmp_word));
                    char_matched.map(each => {
                        let found = []
                        if (typeof each.suggestion === 'string') {
                            found = JSON.parse(each.suggestion)
                        }
                        machine_suggestions = found.concat(machine_suggestions)
                        if (machine_suggestions.length > required_num) {
                            machine_suggestions = machine_suggestions.slice(0, required_num)
                            return;
                        }
                    })
                }

                // Lastly, add any number if less than 4 suggestions after all tries
                // while (machine_suggestions.length < required_num) {
                //     machine_suggestions.push("a")
                // }

                return machine_suggestions
            }

            // Treat as English if it contains Latin letters, contains no Indic letters,
            // and only uses common token chars.
            const checkSuggestion = (word, real_use = true) => { // Not generating machine suggestions when only checking if the word is in wrong word list in the db
                const normalizedWord = normalizeApostrophes(word || '').trim();
                // skip DB lookups & machine suggestions for English tokens
                const isEnglish = isEnglishLikeToken(word) || isEnglishLikeToken(normalizedWord);
                if (isEnglish) return [[], false];
                let found = []
                let current = suggestion.find(each => each.word == normalizedWord || each.word == word)
                let existedInDB = false
                if (current) {
                    found = current.suggestion
                    existedInDB = true
                }
                if (typeof found === 'string') {
                    found = JSON.parse(found);
                }
                
                // Add machine-generated suggestions
                // If we find a match in db then we will not go for the second method (machine-gnenerated suggestions)
                console.log("[checkSuggestion]: ", normalizedWord, " found = ", found, " existedInDB = ", existedInDB, " real_use = ", real_use)
                if (real_use && found.length == 0) {
                    const required_num = SUGGESTION_LIMIT;
                    const machine_generated = getMachineSuggestion(normalizedWord, required_num)
                    found = machine_generated.concat(found)
                }
                let uniqueSuggestions = [...new Set(found)];
                if (uniqueSuggestions.length > SUGGESTION_LIMIT) {
                    uniqueSuggestions = uniqueSuggestions.slice(0, SUGGESTION_LIMIT);
                }
                return [uniqueSuggestions, existedInDB] // remove duplicates
            }

            document.removeEventListener("cut", handleCut, true);
            document.removeEventListener("copy", handleCopy, true);
            document.removeEventListener("paste", handlePaste, true);

            document.addEventListener('paste', handlePaste, true)
            document.addEventListener('copy', handleCopy, true);
            document.addEventListener('cut', handleCut, true);

            const checkMachineSuggestion = (word) => {
                let exist = false;
                if(word!=='' && word!==' '){
                    let working = word.trim();
                    // 1) Strip start-only symbols
                    working = trimWord(working, trimStartSymbolsAll, 'start');
                    // 2) Strip end-only symbols
                    working = trimWord(working, trimEndSymbolsAll,   'end');
                    // 3) Strip both-side symbols
                    working = trimWord(working, trimBothSymbols,   'both');
                    // 4) Remove any unwanted patterns anywhere mid-word
                    working = removeAnywhere(working, trimAnywhereSymbolsNoApos);
                    const normalized = normalizeApostrophes(working);

                    if (isEnglishLikeToken(working) || isEnglishLikeToken(normalized)) {
                        return false;
                    }

                    // First, check if this word is in suggestion, that is, db. If so, always wrong
                    if(isNumeric(normalized))  // if numeric style
                        exist = true;
                    else {
                        switch(language) {
                            case "Assamese":
                                exist = unique_words.includes(` ${normalized} `) || unique_words.includes(`\n${normalized} `) || unique_words.includes(` ${normalized}\n`);
                                break
                            default:
                                exist = unique_words.includes(`${normalized}`) || unique_words.includes(`\n${normalized} `) || unique_words.includes(` ${normalized}\n`);
                                break
                        }
                    }
            
                    if (!exist) { // check if joined word
                        exist = checkJoinedWord(normalized)
                    }

                    if (!exist) { // check variation
                        for (let key in current_constants.variations) {
                            let tmp_word = normalized
                            let values = []
                            if (current_constants.variations[key] instanceof Array) {
                                current_constants.variations[key].map (each => {
                                    values.push(each)
                                })
                            } else {
                                values.push(current_constants.variations[key])
                            }
                            const normalizedKey = normalizeApostrophes(key);
                            const normalizedVals = values.map(each => normalizeApostrophes(each));
                            if (tmp_word.includes(normalizedKey)) {
                                normalizedVals.map(each => {
                                    tmp_word = normalized.replace(normalizedKey, each)
                                    exist = unique_words.includes(` ${tmp_word} `) || unique_words.includes(`\n${tmp_word} `) || unique_words.includes(` ${tmp_word}\n`);
                                    if (!exist) {
                                        exist = checkJoinedWord(tmp_word)
                                        if (exist) {
                                            return;
                                        }
                                    } else return;
                                })
                                if (exist) {
                                    break;
                                }
                            }
                            if (!exist) {
                                tmp_word = normalized
                                normalizedVals.map(each => {
                                    if (tmp_word.includes(each)) {
                                        tmp_word = normalized.replace(each, normalizedKey)
                                        exist = unique_words.includes(` ${tmp_word} `) || unique_words.includes(`\n${tmp_word} `) || unique_words.includes(` ${tmp_word}\n`);
                                        if (!exist) {
                                            exist = checkJoinedWord(tmp_word)
                                            if (exist) {
                                                return;
                                            }
                                        } else return;
                                    }
                                })
                                if (exist) {
                                    break;
                                }
                            }
                        }
                    }
                }

                return exist;
            }
            const checkWord = (word,start_position,end_position, insert_content) => {
                console.log('[checkWord]: ' + 'word = "' + word + '" , insert_content = ' + insert_content);

                let wordCache = wordCacheRef.current;
                const trimmed = (word || '').trim();
                const { cleaned, normalized } = cleanTokenForCheck(trimmed);
                const cacheKey = normalized || trimmed;

                if (ignoredWordsRef.current.has(cacheKey)) {
                    if (typeof start_position === 'number' && typeof end_position === 'number') {
                      quill.formatText(start_position, end_position - start_position, {
                        color: '#111',
                        underline: false
                      });
                    }
                    wordCache.set(cacheKey, true);
                    return;    // bail out early
                }

                // quick bail if we’ve already seen this exact token
                let found = []
                if (wordCache.has(cacheKey)) {
                    const isValid = wordCache.get(cacheKey);
                    if (!isValid) {
                        [found, ] = checkSuggestion(normalized)
                    }
                    quill.formatText(start_position, end_position - start_position, {
                    color:  isValid ? '#111' : 'red',
                    underline: found.length ? true : false
                    });
                    return;
                }

                if (exceptions_in_checking.includes(normalized) && insert_content == ' ') {return;}

                if(normalized!=='' && normalized!==' '){
                    let tmp_word = trimmed;

                    //  If both sides of a hyphened word or a word in either side of / are true, then the word is true (correct).
                    if (normalized.includes('-') || normalized.includes('/') || normalized.includes(':')) {
                        for (let delim of ['-', '/', ':']) {
                          if (!normalized.includes(delim)) continue;
                          const parts = normalized.split(delim).map(p => p.trim()).filter(Boolean);
                          let ok;
                      
                          if (delim === '/') {
                            // slash: at least one side valid
                            ok = parts.some(part => isWordValid(part));
                          } else {
                            // hyphen: both sides must be valid
                            ok = parts.every(part => isWordValid(part));
                          }

                          console.log(`Checking '${normalized}' with delimiter '${delim}':`, ok, parts);
                      
                          if (ok) {
                            quill.formatText(start_position, end_position - start_position, {
                              color: "#111",
                              underline: false
                            });
                            wordCache.set(cacheKey, true);
                            return;
                          }
                        }
                    }

                    const englishLike = isEnglishLikeToken(cleaned) || isEnglishLikeToken(normalized);
                    if (englishLike) {
                        wordCache.set(cacheKey, false);
                        if (typeof start_position === 'number' && typeof end_position === 'number') {
                            quill.formatText(start_position, end_position-start_position, {color:"red", underline: false})
                        }
                        return;
                    }

                    let [, existedInDB] = checkSuggestion(normalized, false)
                    console.log('existedInDB = ', existedInDB, " current word = '" + normalized + "'")
                    if (existedInDB) {
                        quill.formatText(start_position, end_position-start_position, {color:"red", underline: true})
                        wordCache.set(cacheKey, false);
                        return
                    }
            
                    let exist = isWordValid(normalized);

                    console.log('exist = ', exist)
            
                    wordCache.set(cacheKey, exist);
            
                    if(!exist){
                        if (wrong_words.findIndex(each => each == normalized) < 0 && (insert_content == ' ' || insert_content.includes(' '))) wrong_words.push(normalized);
                        if(typeof start_position!=='undefined' && typeof end_position!=='undefined'){
                            // trim spaces before and after this word
                            let i = 0
                            while (tmp_word[i] == ' ' && i < tmp_word.length) {
                                start_position++
                                i++
                            } 
                            i = tmp_word.length - 1
                            while (tmp_word[i] == ' ' && i >= 0) {
                                end_position--
                                i--
                            }

                            [found, ] = checkSuggestion(normalized)
                            let is_underline = false
                            if (found.length > 0) { // Only show underline when it has suggestions
                                is_underline = true
                            }
                            quill.formatText(start_position, end_position-start_position, {color:"red", underline: is_underline})
                        }
                    }
                    else{
                        if(typeof start_position!=='undefined' && typeof end_position!=='undefined') {
                            quill.formatText(start_position, end_position-start_position, {color:"#111", underline: false})
                        }
                    }
            
                } else {
                    if(typeof start_position!=='undefined' && typeof end_position!=='undefined') {
                        quill.formatText(start_position, end_position-start_position, {color:"red", underline: false})
                    }
                }
            }
            // 2) Update prompt function to loop over all mappings:
            function promptCrossLanguageFix() {
                const mappings = crossLangMap[language] || [];
                const text = quill.getText();
            
                for (let { detectChar, replaceChar, detectedName, targetName } of mappings) {
                if (text.includes(detectChar)) {
                    Modal.confirm({
                    title: `${detectedName} detected in your text`,
                    content: `Do you want to change all “${detectedName}” to “${targetName}” and rerun spell‐checking?`,
                    okText: 'Yes',
                    cancelText: 'No',
                    onOk() {
                        changeAll(detectChar, replaceChar, false);
                    }
                    });
                    return true;  // we showed a prompt, so bail out of the scan
                }
                }
            
                return false; // no prompts shown
            }

            const getWholeTextContents = () => {
                var contents=quill.getText();
                return contents;
            }

            const spellCheck = (position, operation_type, insert_content) => {
                console.log(
                    'position=' + position +
                    ', operation_type=' + operation_type +
                    ', insert_content=' + JSON.stringify(insert_content)
                );

                // 1) reset upload timer
                clearTimeout(upload_timer);

                // 2) grab entire text one time
                const contents = quill.getText();

                // 3) compute the absolute start of the insertion block
                let start_position = 0;
                let end_position   = 0;
                if (operation_type === 'delete') {
                    if ((contents[position] === '\n' || contents[position] === ' ') && position > 0) {
                    position--;
                    }
                }
                for (let i = position; i >= 0; i--) {
                    if (contents[i] === ' ' || contents[i] === '\n') {
                    start_position = i;
                    break;
                    }
                }
                if (operation_type === 'insert') {
                    end_position = position + insert_content.length;
                }

                // 4) if it’s a paste (multiple chars or any space),
                //    only scan the inserted chunk itself
                if (insert_content.length > 1 && /\s/.test(insert_content)) {
                    let offset = start_position;
                    // split on whitespace *including* the separators so we keep lengths
                    insert_content.split(/(\s+)/).forEach(chunk => {
                    const word = chunk.trim();
                    if (word) {
                        // account for any leading/trailing whitespace in chunk
                        const frontTrim = chunk.indexOf(word);
                        const wStart    = offset + frontTrim;
                        const wEnd      = wStart + word.length;
                        checkWord(word, wStart, wEnd, insert_content);
                    }
                    offset += chunk.length;
                    });

                } else {
                    // 5) single-char insert/delete
                    // If it’s a space, re‑check both sides of the split:
                    const checkAround = (pos) => {
                        let start = pos, end = pos;
                        // expand backward
                        while (start > 0 && !/\s/.test(contents[start - 1])) start--;
                        // expand forward
                        while (end < contents.length && !/\s/.test(contents[end])) end++;
                        const raw = contents.slice(start, end).trim();
                        if (!raw) return;
                        const front = contents.slice(start, end).indexOf(raw);
                        const realStart = start + front;
                        const realEnd   = realStart + raw.length;
                        checkWord(raw, realStart, realEnd, insert_content);
                    };
    
                    if (insert_content === ' ') {
                        // the space went in at `position`, so
                        //  – previous word ends at position
                        //  – next word starts at position+1
                        checkAround(position - 1);
                        checkAround(position + 1);
                    } else {
                        // non‑space single char: just one token
                        checkAround(position);
                    }
                }

                // 6) restart the timer exactly as before
                console.log('uploadWrongWords')
                console.log(uploadWrongWords)
                upload_timer = setTimeout(uploadWrongWords, 5000);
                };

            // 1) Utility to decide if a char is one we want to preserve
            function isPreservedPunc(ch) {
                // return ch === ',' || ch === '|' || ch === '।' || ch === '-' || ch == '—' || ch == '৷';
                return [',','|','।','-','—','৷','!'].includes(ch);
            }

            // 2) new fixWord (for single-word replacement from the tooltip)
            function fixWord(index, length, newText) {
                // grab the plain text
                const fullText = quill.getText();
                // check for double‑bang first
                let puncLen = 0;
                let puncText = '';
                let hasPunc  = false;
                if (fullText.slice(index + length - 2, index + length) === '!!') {
                    puncLen = 0;
                    puncText = '!!';
                    hasPunc = true;
                } else {
                    const ch = fullText[index + length - 1];
                    hasPunc  = isPreservedPunc(ch);
                    if (hasPunc) {
                        // puncLen = 1;
                        puncLen = 0;
                        puncText = ch;
                    }
                }
                const deleteLen = length + puncLen;
                // build a Delta: retain up to the word, delete it (and punc),
                // then re-insert newText + punc with correct styling
                const goodAttrs = { color: '#111', underline: false };
                const delta = new Delta()
                    .retain(index)
                    .delete(deleteLen)
                    .insert(newText + puncText, goodAttrs);

                quill.updateContents(delta, 'user');

                // move cursor to just after the insertion
                quill.focus();
                quill.setSelection(
                    index + newText.length + (hasPunc ? 0 : 0),
                    0
                );
            }

            quill.on("selection-change", (range) => {

                let newText; // link text
                let index; // link start index
                let length; // link text length
                let linkValue; // link href value

                // check if this is a wrong word
                if (!range || !quill || !quill.getFormat(range)['underline']) return
                
                // find word
                index = range.index
                let contents=getWholeTextContents();
                let pos_start = 0, pos_end = 0
                for (let i = index; i >= 0; i--) {
                    if (contents[i] == ' ' || contents[i] == '\n') {
                        pos_start = i + 1
                        break
                    }
                }
                for (let i = index; i < contents.length; i++) {
                    if (contents[i] == ' ' || contents[i] == '\n') {
                        pos_end = i
                        break
                    }
                }
                let found = contents.substring(pos_start, pos_end)
                found=found.trim();
                // Need checking why this is required !!!!
                // 1) Strip start-only symbols
                found = trimWord(found, trimStartSymbolsAll, 'start');
                // 2) Strip end-only symbols
                found = trimWord(found, trimEndSymbolsAll,   'end');
                // 3) Strip both‐side symbols
                found = trimWord(found, trimBothSymbols,   'both');
                // 4) Remove any unwanted patterns anywhere mid‐word
                found = removeAnywhere(found, trimAnywhereSymbolsNoApos);
                // !!!!
                if (pos_end == 0 || pos_end - pos_start < 1) return
                // end of find word

                tooltip.root.classList.add("no-before") // remove default ::before attribute of tooltip
                // remove Edit & Remove buttons and preview div  which are default ones in Link blot
                const edit_element = document.getElementsByClassName("ql-action")[0];
                if(edit_element){
                    edit_element.parentNode.removeChild(edit_element);
                }
                const remove_element = document.getElementsByClassName("ql-remove")[0];
                if(remove_element){
                    remove_element.parentNode.removeChild(remove_element);
                }
                const prev_element = document.getElementsByClassName("ql-preview")[0];
                if(prev_element){
                    prev_element.parentNode.removeChild(prev_element);
                }

                // remove all ex-buttons
                const elements = document.getElementsByClassName("link-text-container");
                while(elements.length > 0){
                    elements[0].parentNode.removeChild(elements[0]);
                }

                tooltip.root.classList.remove("ql-hidden");

                // create button element
                let [candidates, ] = checkSuggestion(found)

                // remove old buttons (if any)
                tooltip.root
                    .querySelectorAll('.suggestion-item')
                    .forEach(el => el.remove());

                candidates.forEach(candidate => {
                    // 1) container for this suggestion + its “All” button
                    const item = document.createElement('div');
                    item.className = 'suggestion-item';
                    // flex box so text is left, icon is right
                    item.style.display = 'flex';
                    item.style.alignItems = 'center';
                    item.style.justifyContent = 'space-between';
                    item.style.padding = '4px 8px';
                    
                    // 2) the suggestion button itself
                    const suggBtn = document.createElement('button');
                    suggBtn.className = 'suggest-btn';
                    suggBtn.innerText = candidate;
                    suggBtn.onclick = () => {
                        fixWord(pos_start, pos_end - pos_start, candidate);
                        tooltip.hide();
                    };
                    item.appendChild(suggBtn);
                    
                    // 3) the “Change All” icon, hidden by default
                    const allBtn = document.createElement('button');
                    allBtn.className = 'change-all-icon';
                    allBtn.innerText = '🔁';             // we can swap for any icon/text
                    allBtn.title = 'Change All';
                    allBtn.style.border = 'none';
                    allBtn.style.background = 'none';
                    allBtn.style.cursor = 'pointer';
                    allBtn.style.visibility = 'hidden';  // hide until hover
                    allBtn.onclick = () => {
                        changeAll(found, candidate, true);
                        tooltip.hide();
                        // put cursor right after this word:
                        quill.focus();
                        quill.setSelection(pos_start + candidate.length, 0);
                    };
                    item.appendChild(allBtn);

                    // 4) show the icon on hover
                    item.addEventListener('mouseenter', () => {
                        allBtn.style.visibility = 'visible';
                    });
                    item.addEventListener('mouseleave', () => {
                        allBtn.style.visibility = 'hidden';
                    });
                    
                    // 5) append it into the tooltip
                    tooltip.root.appendChild(item);
                });

                // 1) remove old buttons (if any)
                const oldChange = tooltip.root.querySelector(".change-all-btn");
                if (oldChange) oldChange.remove();
                const oldIgnore = tooltip.root.querySelector(".ignore-all-btn");
                if (oldIgnore) oldIgnore.remove();

                const ignoreAllBtn = document.createElement("button");
                ignoreAllBtn.className = "ignore-all-btn";
                ignoreAllBtn.innerText = "Ignore All";
                ignoreAllBtn.style.display = "block";
                ignoreAllBtn.style.margin = "4px 0";
                ignoreAllBtn.onclick = () => {
                    ignoreAll(found);
                    tooltip.hide();
                    tooltip.root.style.display = 'none';
                };
                tooltip.root.appendChild(ignoreAllBtn);

                // Keyboard selection
                // 2) Collect *all* interactive items: suggestion-items + ignoreAllBtn
                const suggestionItems = Array.from(tooltip.root.querySelectorAll(".suggestion-item"));
                const allButtons      = [...suggestionItems, ignoreAllBtn];

                let selectedIndex = -1;
                function updateHighlight(newIndex) {
                    if (selectedIndex >= 0) {
                    allButtons[selectedIndex].classList.remove("selected");
                    }
                    selectedIndex = newIndex;
                    allButtons[selectedIndex].classList.add("selected");
                    allButtons[selectedIndex].scrollIntoView({ block: "nearest" });
                }

                // 3) Wire keyboard on the tooltip
                tooltip.root.tabIndex = 0;
                tooltip.root.focus();
                tooltip.root.addEventListener("keydown", e => {
                    if (e.key === "ArrowDown") {
                    e.preventDefault();
                    updateHighlight((selectedIndex + 1) % allButtons.length);
                    }
                    else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    updateHighlight((selectedIndex - 1 + allButtons.length) % allButtons.length);
                    }
                    else if (e.key === "Enter" && selectedIndex >= 0) {
                    e.preventDefault();
                    const btn = allButtons[selectedIndex];
                    // if it’s a suggestion-item, trigger its child .suggest-btn
                    if (btn.classList.contains("suggestion-item")) {
                        btn.querySelector(".suggest-btn").click();
                    } else {
                        // it must be the ignoreAllBtn
                        btn.click();
                    }
                    }
                    else if (e.key === "Escape") {
                    tooltip.hide();
                    tooltip.root.style.display = "none";
                    }
                });

                // 4) Mouse hover still sets selection
                allButtons.forEach((el,i) => {
                    el.addEventListener("mouseenter", () => updateHighlight(i));
                });

                // 5) Kick off with the first suggestion selected (or ignoreAll if no suggestions)
                updateHighlight(suggestionItems.length ? 0 : suggestionItems.length);
                // End of Keyboard Selection

                var sel = quill.root.ownerDocument.getSelection();
                if (sel && sel.rangeCount > 0) {
                    var selrg = sel.getRangeAt(0);
                    if (selrg) {
                        var rects = selrg.getClientRects();
                        if (rects.length > 0) {
                            const ql_editor = document.getElementsByClassName("ql-editor")[0].getBoundingClientRect()
                            const ql_tooltip = document.getElementsByClassName("ql-tooltip")[0]
                            let scrollTop = quill.root.scrollTop; // Get the scroll offset of the editor
                            ql_tooltip.style.position = 'absolute'

                            ql_tooltip.style.top = (rects[0].top - ql_editor.y - ql_editor.height / 20 + 20 + scrollTop) + 'px';
                            ql_tooltip.style.left = (rects[0].left - ql_editor.x - ql_editor.width / 20) + 'px';
                        }
                    }
                }

                // Add “Change All” & “Ignore All” for this misspelled word:
                tooltip.root.style.display        = 'flex';
                tooltip.root.style.flexDirection  = 'column';
                tooltip.root.style.alignItems     = 'center';

                tooltip.root.setAttribute('tabindex', '0');
                tooltip.root.setAttribute('z-index', 100);
                tooltip.root.focus();
            })

            quill.on('text-change', async function(delta, oldDelta, source) {
                if (source == 'user') {
                    // 1) Extract everything they just inserted
                    const insertedText = delta.ops
                    .filter(op => typeof op.insert === 'string')
                    .map(op => op.insert)
                    .join('');

                    let ops=delta.ops;
                    let retain=0, operation_type='', insert_content='';
                    // Get The Operation Type, only operation type is insert or delete, spell checking will be done.
                    for(let i=0;i<ops.length;i++){
                        if(typeof ops[i].retain!='undefined')
                        retain=ops[i].retain;
                        if(typeof ops[i].delete!='undefined')
                        {
                            operation_type='delete';
                        }
                        if(typeof ops[i].insert!='undefined')
                        { 
                            operation_type='insert';
                            insert_content+=ops[i].insert;
                        }
                    }

                    // 2) If we haven’t yet prompted, and the insertion contains a mapped char…
                    if (!crossCheckedRef.current) {
                        const mappings = crossLangMap[language] || [];
                        if (mappings.some(({ detectChar }) => insertedText.includes(detectChar))) {
                            crossCheckedRef.current = true;
                            spellCheckPausedRef.current = true;
                            Modal.confirm({
                            title: 'Assamese character detected',
                            content: 'Replace all ৰ before spell-checking?',
                            okText: 'Yes',
                            cancelText: 'No',
                            onOk() {
                                mappings.forEach(({ detectChar, replaceChar }) => {
                                    changeAll(detectChar, replaceChar, /* exactMatch */ true);
                                });
                                spellCheckPausedRef.current = false;
                                if (insert_content.length > 1) {
                                    setLoading(true);
                                    // let spinner appear
                                    setTimeout(async () => {
                                        await new Promise(r => setTimeout(r, 100));
                                        if(operation_type==='insert' || operation_type==='delete'){
                                            spellCheck(retain,operation_type, insert_content);
                                            setLoading(false);
                                        }
                                    }, 0);
                                }
                            },
                            onCancel() {
                                spellCheckPausedRef.current = false;
                                if (insert_content.length > 1) {
                                    setLoading(true);
                                    // let spinner appear
                                    setTimeout(async () => {
                                        await new Promise(r => setTimeout(r, 100));
                                        if(operation_type==='insert' || operation_type==='delete'){
                                            spellCheck(retain,operation_type, insert_content);
                                            setLoading(false);
                                        }
                                    }, 0);
                                }
                            }
                            });
                            return; // <-- bail: don’t call spellCheck while modal is open
                        }
                    }

                    //    End Getting    //
                    if (insert_content.length > 1) {
                        setLoading(true);
                        // let spinner appear
                        await new Promise(r => setTimeout(r, 100));
                    }
                    if(operation_type==='insert' || operation_type==='delete'){
                        spellCheck(retain,operation_type, insert_content);
                    }
                }
                updateWordCount();
                setLoading(false);
            });
        }

        return () => {
            // 
          };
      }, [quill]);

    // Fast (no-suggestion) checker using a separate Quill box
    useEffect(() => {
        if (!fastQuill || !Quill) return;

        const getFastDeltaRange = (delta) => {
            let index = 0;
            let start = null;
            let end = null;
            let insertedText = '';
            let hasChange = false;

            (delta?.ops || []).forEach(op => {
                if (op.retain) {
                    index += op.retain;
                    return;
                }
                if (op.insert) {
                    hasChange = true;
                    const insertLength = typeof op.insert === 'string' ? op.insert.length : 1;
                    if (start === null) start = index;
                    end = index + insertLength;
                    if (typeof op.insert === 'string') insertedText += op.insert;
                    index += insertLength;
                    return;
                }
                if (op.delete) {
                    hasChange = true;
                    if (start === null) start = index;
                    end = index;
                }
            });

            return {
                start: start ?? 0,
                end: end ?? (start ?? 0),
                insertedText,
                hasChange
            };
        };

        const formatFastRange = (text, rangeStart = 0, rangeEnd = null) => {
            if (!text) return;
            const length = text.length;
            let start = Math.max(0, Math.min(rangeStart ?? 0, length));
            let end = Math.max(start, Math.min((rangeEnd ?? length), length));
            if (start === end) return;
            const segment = text.slice(start, end);
            let offset = start;
            segment.split(/(\s+)/).forEach(chunk => {
                const len = chunk.length;
                if (!len) return;
                const token = chunk.trim();
                if (!token) {
                    fastQuill.formatText(offset, len, { color: '#111', underline: false, background: false }, 'silent');
                } else {
                    const { normalized, valid } = validateWithoutSuggestions(chunk);
                    fastQuill.formatText(offset, len, { color: valid ? '#111' : 'red', underline: false, background: false }, 'silent');
                }
                offset += len;
            });
        };

        const handleFastCopy = (e) => {
            if (!e || !e.clipboardData) return;
            if (!fastQuill || !fastQuill.root) return;
            if (e.target && !fastQuill.root.contains(e.target)) return;
            const range = fastQuill.getSelection();
            const text = range ? fastQuill.getText(range.index, range.length) : '';
            const newText = preActionCopy(text).replace(/\s+$/, '');
            e.clipboardData.setData('text/plain', newText);
            e.preventDefault();
        };

        const handleFastCut = (e) => {
            if (!e || !e.clipboardData) return;
            if (!fastQuill || !fastQuill.root) return;
            if (e.target && !fastQuill.root.contains(e.target)) return;
            const range = fastQuill.getSelection();
            if (!range || range.length === 0) return;
            const text = fastQuill.getText(range.index, range.length);
            const newText = preActionCopy(text);
            fastQuill.deleteText(range.index, range.length, 'user');
            e.clipboardData.setData('text/plain', newText);
            e.preventDefault();
        };

        const handleFastPaste = (e) => {
            if (!e || !e.clipboardData) return;
            if (!fastQuill || !fastQuill.root) return;
            if (e.target && !fastQuill.root.contains(e.target)) return;
            e.preventDefault();
            const raw = e.clipboardData.getData('text/plain');
            fastPasteInProgressRef.current = true;
            setFastLoading(true);
            const inserted = insertFastPlainText(raw);
            if (!inserted) {
                fastPasteInProgressRef.current = false;
                setFastLoading(false);
                fastQuill.focus();
            }
        };

        const handleFastChangeCore = (delta, oldDelta, source) => {
            if (fastAdjustingRef.current) return;
            const selection = fastQuill.getSelection();
            const restoreSelection = () => {
                if (!selection || source !== 'user') return;
                const maxIndex = Math.max(0, fastQuill.getLength() - 1);
                const nextIndex = Math.min(selection.index, maxIndex);
                fastQuill.setSelection(nextIndex, selection.length || 0, 'silent');
            };
            const { start, end, insertedText, hasChange } = getFastDeltaRange(delta);
            if (!hasChange) {
                restoreSelection();
                return;
            }
            if (source === 'user') {
                maybePromptFastCrossLanguage(insertedText);
            }
            const text = fastQuill.getText();
            const wc = countWords(text);
            setFastWordCount(wc);
            const includeLastToken = /\s/.test(insertedText);
            if (wc > FAST_WORD_LIMIT) {
                fastAdjustingRef.current = true;
                const trimmed = trimToWordLimit(text, FAST_WORD_LIMIT);
                fastQuill.setText(trimmed);
                fastAdjustingRef.current = false;
                setFastWordCount(countWords(trimmed));
                formatFastRange(trimmed, 0, trimmed.length);
                collectFastWrongWords(trimmed, includeLastToken);
                clearTimeout(upload_timer);
                upload_timer = setTimeout(uploadWrongWords, 5000);
                restoreSelection();
                return;
            }
            const { start: rangeStart, end: rangeEnd } = expandTokenRange(text, start, end);
            formatFastRange(text, rangeStart, rangeEnd);
            collectFastWrongWords(text, includeLastToken, rangeStart, rangeEnd);
            clearTimeout(upload_timer);
            upload_timer = setTimeout(uploadWrongWords, 5000);
            restoreSelection();
        };

        const handleFastChange = (delta, oldDelta, source) => {
            if (fastPasteInProgressRef.current) {
                setFastLoading(true);
                setTimeout(async () => {
                    // Let the spinner render before doing heavier work.
                    await new Promise(r => setTimeout(r, 60));
                    handleFastChangeCore(delta, oldDelta, source);
                    fastPasteInProgressRef.current = false;
                    setFastLoading(false);
                }, 0);
                return;
            }
            handleFastChangeCore(delta, oldDelta, source);
        };

        fastQuill.root.addEventListener('copy', handleFastCopy);
        fastQuill.root.addEventListener('cut', handleFastCut);
        fastQuill.root.addEventListener('paste', handleFastPaste, true);
        fastQuill.on('text-change', handleFastChange);
        const initialText = fastQuill.getText();
        setFastWordCount(countWords(initialText));
        formatFastRange(initialText, 0, initialText.length);

        return () => {
            fastQuill.off('text-change', handleFastChange);
            fastQuill.root.removeEventListener('copy', handleFastCopy);
            fastQuill.root.removeEventListener('cut', handleFastCut);
            fastQuill.root.removeEventListener('paste', handleFastPaste, true);
        };
    }, [fastQuill, Quill]);

    useEffect(() => {
        const adNode = betweenBoxesAdRef.current;
        if (!adNode || typeof window === 'undefined') return;

        const loadAd = () => {
            if (adNode.dataset.adsInitialized === 'true') return;

            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
                adNode.dataset.adsInitialized = 'true';
            } catch (error) {
                // Ignore duplicate or late ad initialization attempts.
            }
        };

        loadAd();
        const retryTimer = setTimeout(loadAd, 1200);

        return () => clearTimeout(retryTimer);
    }, []);

    const handleFastCopyClick = () => {
        if (!fastQuill) return;
        const text = preActionCopy(fastQuill.getText()).trim();
        navigator.clipboard.writeText(text).then(() => {
            fastQuill.focus();
        });
    };

    const maybePromptFastCrossLanguage = (textToCheck = '') => {
        if (!fastQuill || fastCrossCheckedRef.current) return false;
        const mappings = crossLangMap[language] || [];
        const offenders = mappings.filter(({ detectChar }) => textToCheck.includes(detectChar));
        if (!offenders.length) return false;
        fastCrossCheckedRef.current = true;
        const [primary] = offenders;
        Modal.confirm({
            title: `${primary.detectedName} detected`,
            content: `Replace all ${primary.detectChar} before spell-checking?`,
            okText: 'Yes',
            cancelText: 'No',
            onOk() {
                const selection = fastQuill.getSelection();
                const text = fastQuill.getText();
                let nextText = text;
                offenders.forEach(({ detectChar, replaceChar }) => {
                    nextText = nextText.split(detectChar).join(replaceChar);
                });
                if (nextText !== text) {
                    fastQuill.setText(nextText);
                    if (selection) {
                        const maxIndex = Math.max(0, fastQuill.getLength() - 1);
                        const nextIndex = Math.min(selection.index, maxIndex);
                        fastQuill.setSelection(nextIndex, selection.length || 0, 'silent');
                    } else {
                        fastQuill.setSelection(Math.max(0, fastQuill.getLength() - 1), 0, 'silent');
                    }
                }
            }
        });
        return true;
    };

    const handleFastPasteClick = async () => {
        if (!fastQuill) return;
        fastPasteInProgressRef.current = true;
        setFastLoading(true);
        try {
            const raw = await navigator.clipboard.readText();
            const inserted = insertFastPlainText(raw);
            if (!inserted) {
                fastPasteInProgressRef.current = false;
                setFastLoading(false);
                fastQuill.focus();
            }
        } catch (err) {
            console.error(err);
            fastPasteInProgressRef.current = false;
            setFastLoading(false);
        }
    };

    const handleFastClearClick = () => {
        if (!fastQuill) return;
        fastQuill.setText('');
        setFastWordCount(0);
        fastCrossCheckedRef.current = false;
        fastQuill.focus();
    };

    const handleCopy = (e) => {
        if (!e || !e.clipboardData) return;
        if (!quill || !quill.root) return;
        if (e.target && !quill.root.contains(e.target)) return;
        const range = quill.getSelection();
        if (!range || range.length === 0) return;
        const text = quill.getText(range.index, range.length);
        // Trim trailing whitespace/newlines so Ctrl+C matches the Copy button behavior
        const new_text = preActionCopy(text).replace(/\s+$/, '');
        if (e.clipboardData) {
            e.clipboardData.setData('text/plain', new_text);
            e.preventDefault();
        }
    }

    const uploadWrongWords = () => {
        
        const options = {
          method: 'POST',
          url: 'spellChecker/WrongWord',
          headers: {
            'content-type': 'application/json;charset=UTF-8',
          },
        data: {
            wrong_words: JSON.stringify(wrong_words),
            lang: language,
        },
        };
        
        axios.request(options).then(function (response) {
            
        }).catch(function (error) {
            console.error(error);
        });

    }

    const preActionCopy = (data) => {

        let new_data = data;
        
        current_constants.fix_patterns.map(pattern=>{
            new_data = new_data.split(pattern.search_word).join(pattern.replace_word);
        });
    
        return new_data;
    }

    const saveUniqueWords = (data) => {
        if (!data) {
            unique_words = '';
            return;
        }
        unique_words = data;
        current_constants.patterns.map(pattern=>{
            unique_words=unique_words.split(pattern.search_word).join(pattern.replace_word);
        })
    }

    // 1) FULL symbol lists, combining existing patterns + extras

    // — only ever peel these off if they’re at the START of the token
    const trimStartSymbols = [ // we should place missing symbols from longest to shortest
        // quoted-letter combos that begin with a quote:
        '"ক','"কে','"কো','"ৰ','"ৰে','"ৰেই','"ৰো',
        '"লৈ','"লৈও','"ত','"তে','"তো','"নো','"হে',
        '"','“','‘',  // opening quotes (removed "'")
        '(','[','{','<','৺',
    ];
    
    // — only ever peel these off if they’re at the END of the token
    const trimEndSymbols = [ // we should place missing symbols from longest to shortest
        // multi‐char end‐clusters:
        '),',');',')।',')৷','!)','।)','৷)', '!!',
        '],','];',']।',']৷','!]','।]','৷]',']ৰ',
        '},','};','}।','}৷','!}','।}','৷}','}ৰ','।"','?"','!”','!"','?”','।’’','।”','?’’',"।'","।’","?’",'—',"?'","!’","!'",'।।','??','???', //some ('।"','?"',"।'","।’","?’","?'","!’","!'",) are added anew
        // odd bracket+quote clusters:
        '’,','",',
        ')',']','}','>','%',    // closing brackets & percent
        ',','.','?',';',':','!','৷','।','”','’','॰',
        '–','-','/','\\','|','#','@','$','&',
    ];
    
    const trimStartSymbolsAll = [...trimStartSymbols, '‘',"'",'“','”','"','\u200C'];
    const trimEndSymbolsAll = [...trimEndSymbols, '‘','’',"'",'“','”','"','\u2026','\u2026\u2026','\u200C','….',"?’","!’"];

    // — strip these at EITHER start or end
    const trimBothSymbols = [ // we should place missing symbols from longest to shortest
        '!!!','!!','...','।৷','।।',
        '(!)','(?)','[!]','[?]','{!}','{?}',
    ];
    
    // — remove these anywhere *inside* the token
    const trimAnywhereSymbols = [ // we should place missing symbols from longest to shortest
        // any of the quoted‐letter combos (just to be safe anywhere)
        '"ক','"কে','"কো','"ৰ','"ৰে','"ৰেই','"ৰো',
        '"লৈ','"লৈও','"ত','"তে','"তো','"নো','"হে',
        // and the bracket‐clusters in case they sit mid‐word:
        '’,','",',
        // single‐char mid‐word quotes/apostrophes:
        '"',"'",'“','”','‘','’','\u200C',
        // percent, pipes, slashes, etc., if they appear in the middle:
        '%','|','/','\\','#','@','$','&','\u200C',
        // hyphens inside words (e.g. তুমি-আমি → তুমি আমি)
        // '-','–', // should be commented for minus equations and hyphened (not hyphened by users) word detection
        // dots inside numbers or abbreviations:
        '.',
    ];

    const trimAnywhereSymbolsNoApos = trimAnywhereSymbols.filter(sym => !["'", "\u2019", "\u2018"].includes(sym));
    

    // 2) The helpers
    
    /**
     * Trim patterns at start/end/both of a token.
     * @param {string} w
     * @param {string[]} patterns
     * @param {'start'|'end'|'both'} where
     */
    function trimWord(str, patterns, where = 'both') {
        let w = str;
        for (let p of patterns) {
          if ((where === 'start' || where === 'both') && w.startsWith(p)) {
            w = w.slice(p.length);
          }
          if ((where === 'end'   || where === 'both') && w.endsWith(p)) {
            w = w.slice(0, w.length - p.length);
          }
        }
        return w;
    }
    
    /**
     * Remove any occurrence of these patterns anywhere in the string.
     * @param {string} w
     * @param {string[]} patterns
     */
    function removeAnywhere(str, patterns) {
        let w = str;
        for (let p of patterns) {
          // split/join is faster than regex replace for literal substrings
          w = w.split(p).join('');
        }
        return w;
    }

    const cleanTokenForCheck = (raw = '') => {
        let cleaned = raw.trim();
        cleaned = trimWord(cleaned, trimStartSymbolsAll, 'start');
        cleaned = trimWord(cleaned, trimEndSymbolsAll, 'end');
        cleaned = trimWord(cleaned, trimBothSymbols, 'both');
        cleaned = removeAnywhere(cleaned, trimAnywhereSymbolsNoApos);
        const normalized = normalizeApostrophes(cleaned);
        return { cleaned, normalized };
    };

    const isFlaggedWrongInDB = (word) => {
        const normalized = normalizeApostrophes(word || '');
        return suggestion.some(each => each.word === normalized || each.word === word);
    };

    const validateWithoutSuggestions = (rawToken) => {
        const { cleaned, normalized } = cleanTokenForCheck(rawToken);
        if (!normalized) return { cleaned: normalized, valid: true };
        const isEnglish = isEnglishLikeToken(cleaned) || isEnglishLikeToken(normalized);
        if (isEnglish) return { cleaned: normalized, valid: false };
        if (isFlaggedWrongInDB(normalized)) return { cleaned: normalized, valid: false };

        if (normalized.includes('-') || normalized.includes('/') || normalized.includes(':')) {
            for (let delim of ['-', '/', ':']) {
                if (!normalized.includes(delim)) continue;
                const parts = normalized.split(delim).map(p => p.trim()).filter(Boolean);
                if (!parts.length) continue;
                const ok = delim === '/'
                    ? parts.some(part => isWordValid(part))
                    : parts.every(part => isWordValid(part));
                if (ok) return { cleaned: normalized, valid: true };
            }
        }

        return { cleaned: normalized, valid: isWordValid(normalized) };
    };

    const expandTokenRange = (text = '', startIndex = 0, endIndex = null) => {
        const length = text.length;
        if (!length) return { start: 0, end: 0 };
        let start = Math.max(0, Math.min(startIndex ?? 0, length));
        let end = Math.max(start, Math.min((endIndex ?? length), length));
        while (start > 0 && !/\s/.test(text[start - 1])) start--;
        while (end < length && !/\s/.test(text[end])) end++;
        return { start, end };
    };

    const collectFastWrongWords = (text = '', includeLastToken = false, rangeStart = 0, rangeEnd = null) => {
        if (!text) return;
        const { start, end } = expandTokenRange(text, rangeStart, rangeEnd);
        if (start === end) return;
        const segment = text.slice(start, end);
        const chunks = segment.split(/(\s+)/);
        const hasTrailingWhitespace = /\s$/.test(text);
        const isAtTextEnd = end >= text.length;
        const allowLastToken = !isAtTextEnd || includeLastToken || hasTrailingWhitespace;

        chunks.forEach((chunk, index) => {
            if (!chunk || /^\s+$/.test(chunk)) return;
            if (!allowLastToken && index === chunks.length - 1) return;

            const { cleaned, normalized } = cleanTokenForCheck(chunk);
            if (!normalized) return;
            const englishLike = isEnglishLikeToken(cleaned) || isEnglishLikeToken(normalized);
            if (englishLike) return;
            if (isFlaggedWrongInDB(normalized)) return;

            const { valid } = validateWithoutSuggestions(chunk);
            if (valid) return;

            if (wrong_words.findIndex(each => each === normalized) < 0) {
                wrong_words.push(normalized);
            }
        });
    };

    // 3) Numeric‐pattern regexes covering all our cases:
    const abDigits = '\\u09E6-\\u09EF';  // Assamese/Bengali ০–৯
    const hiDigits = '\\u0966-\\u096F';  // Hindi ०–९
    const allDigits = `0-9${abDigits}${hiDigits}`;

    const numericPatterns = [
    new RegExp(`^[${allDigits}]+$`, 'u'),                              // pure digits
    new RegExp(`^[${allDigits}]+[-–—][${allDigits}]+$`, 'u'),          // hyphen ranges
    new RegExp(`^[${allDigits}]+(?:[\\/\\\\][${allDigits}]+)+$`, 'u'), // slash/backslash delimited
    new RegExp(`^[${allDigits}]+\\.[${allDigits}]+$`, 'u'),            // decimals
    // new RegExp(`^[${allDigits}]+\\+[${allDigits}]+=+[${allDigits}]+$`,'u'), // plus‐equals
    // new RegExp(`^[${allDigits}]+\\-[${allDigits}]+=+[${allDigits}]+$`, 'u'), // minus‐equals
    new RegExp(`^[${allDigits}]+[+\\-][${allDigits}]+=+[${allDigits}]+$`,'u'), // plus and minus equals like 2+3=5 and 8-3=5
    new RegExp(`^[${allDigits}]+%$`, 'u'),                             // percentage
    new RegExp(`^[${allDigits}]+(?:তম|ৰ|ৰো|ৰে|ত|তে|তো|লৈ|লৈয়ে|লৈকে|লৈও|ক|কে|কৈ|কৈহে|কো|টা|টাৰে|টাৰো|খন|খনে|খনৰ|খনকৈ|খনো|খনেও)$`, 'u'),            // ordinals/suffixes
    ];

    function isNumeric(s) {
        return numericPatterns.some(re => re.test(s));
    }

    const handleChange = (value) => {
        language = value
        wrong_words = []
        current_constants = Constants[language]
        suggestion = [];
        saveUniqueWords(dictionaries[language])
        getSuggestion()

        // Clear the text box
        if (quill) {
            quill.setText(''); // Clear the Quill editor content
            quill.focus();
        }
        if (fastQuill) {
            fastQuill.setText('');
            setFastWordCount(0);
        }
        setWordCount(0);
        fastCrossCheckedRef.current = false;
        wordCacheRef.current.clear();
        ignoredWordsRef.current.clear()

        // load joined words
        const categories = categoriesRef.current;
        const subCategories = subCategoriesRef.current;
        const suffixes = suffixesRef.current;
        const wCategories = wCategoriesRef.current;
        const wSubCategories = wSubCategoriesRef.current;
        const wSuffixes = wSuffixesRef.current;

        for(let i = 0; i < categories.length; i++) {
            categories[i] = wCategories[i][language] || [];
            subCategories[i] = wSubCategories[i][language] || [];
            suffixes[i] = wSuffixes[i][language] || [];
        }
    };

    const getSuggestion = () => {
        const options = {
            method: 'POST',
            url: 'spellChecker/suggestion',
            data: {lang: language},
            headers: {
              'content-type': 'application/json;charset=UTF-8',
            },
          };
            
        axios.request(options).then(function (response) {
            suggestion = response.data
        }).catch(function (error) {
            console.error(error);
        });
    }

    return(
        <Spin spinning={loading} tip="Spell-checking…">
          <div className="w-full flex justify-center pb-24 px-4 md:px-0">
                <div className="flex flex-col w-full gap-3 mt-[30px]">
                        <Alert
                            // message="Warning!"
                            description="Warning! Word limit 413. Exceeding words will not be pasted"
                            type="warning"
                            showIcon
                            closable
                        />
                        <div id="toolbar">
                                                <Select
                                                    defaultValue="Assamese"
                                                    style={{ width: 120 }}
                                                    onChange={handleChange}
                                                    options={dict_names.map(name => ({
                                                    value: name,
                                                    label: name,
                                                    disabled: disabled_names.includes(name) // language names disable conditionally
                                                    }))}
                                                />
                                                </div>
                        <div ref={quillRef} className="h-[300px] lg:h-[260px] mb-2"></div>
                        <div className="text-sm text-gray-600">Words: {wordCount}/{WORD_LIMIT}</div>

                        <Space>
                        <Button
                            onClick={() => {
                                const text = preActionCopy(quill.getText()).trim(); // Trim the text to remove extra newline
                                navigator.clipboard.writeText(text).then(() => {
                                    quill.focus(); // Focus the text box after copying
                                });
                            }}
                        >
                            Copy
                        </Button>
                        <Button id="paste-btn">Paste</Button>
                            <Button
                                onClick={() => {
                                    quill.setText(''); // Clear the text box
                                    ignoredWordsRef.current.clear()         // forget all ignore rules
                                    wordCacheRef.current.clear();           // re-allow every word to be rechecked
                                    wrong_words = [];            // clear any pending uploads
                                    crossCheckedRef.current = false; // allow cross-language prompts again
                                    setWordCount(0);
                                    quill.focus(); // Focus the text box after clearing
                                }}
                            >
                                Clear
                            </Button>
                            <Button id="recheck-btn" hidden>Re-check</Button>
                        </Space>
                        
                            <div className="main-container">
                           {/* Ad between text boxes */}
                            <AdUnit slot="8127041333" />
                            </div>
                    
                
                        <div className="mt-12 border-t pt-6">
                            <h3 className="text-lg font-semibold">Spell check without suggestions</h3>
                            <Spin spinning={fastLoading} tip="Spell-checking...">
                                <div ref={fastQuillRef} className="min-h-[180px] h-auto mb-4 border"></div>
                            </Spin>
                            
                            <div className="text-xs text-gray-600 mb-1">Words: {fastWordCount}/{FAST_WORD_LIMIT}</div>
                            <div className="w-full flex justify-start">
                                <Space>
                                    <Button onClick={handleFastCopyClick}>Copy</Button>
                                    <Button onClick={handleFastPasteClick}>Paste</Button>
                                    <Button onClick={handleFastClearClick}>Clear</Button>
                                </Space>
                            </div>
                            <p className="text-xs text-gray-600">This box for faster performance, so suggestions are skipped.</p>
                        </div>
                        {/* <ReactQuill 
                            value={value} 
                            onChange={handleChange} 
                            modules={{
                                toolbar: [
                                [{ header: [1, 2, false] }],
                                ['bold', 'italic', 'underline'],
                                ['image', 'code-block']
                                ],
                                keyboard: {
                                    // bindings: bindings
                                }
                            }}
                            theme="snow"
                        /> */}
                    </div>
            </div>
        </Spin>
    );
}
